/**
 * SessionProviderLayout component - Main authenticated app layout
 * Wraps authenticated routes with sidebar, header, and session management
 * Refactored to use new layout components with chat widget and status bar
 */
import { SidebarProvider } from "@/components/ui/sidebar"
import * as React from "react"
import { AppSidebar } from "./sidebar/app-sidebar"
import { AppMobileTopBar } from "./app-mobile-top-bar"

/**
 * SessionProviderLayout component props
 */
interface SessionProviderLayoutProps {
  /**
   * Child components to render in the main content area
   */
  children: React.ReactNode
  /**
   * Optional custom class name
   */
  className?: string
  /**
   * Optional custom header title (overrides breadcrumbs)
   */
  headerTitle?: string
  /**
   * Show search bar in header
   */
  showSearch?: boolean
  /**
   * Additional header actions
   */
  headerActions?: React.ReactNode
  /**
   * Custom sidebar props
   */
  sidebarProps?: React.ComponentProps<typeof AppSidebar>
}

/**
 * SessionProviderLayout component - Authenticated app layout wrapper
 * Provides consistent layout structure for all authenticated routes
 * Includes sidebar navigation, header with breadcrumbs, and main content area
 *
 * @param props - Component props
 * @returns JSX element containing the authenticated app layout
 */
export function SessionProviderLayout({
  children,
  sidebarProps,
}: SessionProviderLayoutProps) {
  // Workspaces are now loaded automatically during login/session restoration
  // No need to fetch them separately in the layout
  return (
    <SidebarProvider className="size-full" style={{ "--sidebar-width": "280px" } as React.CSSProperties}>
      <AppSidebar {...sidebarProps} />
      <div className="flex items-stretch flex-1 flex-col overflow-hidden">
        <AppMobileTopBar />
        <div className="flex-1 overflow-auto flex items-stretch bg-background">
          <div className="flex-1 flex-col overflow-auto min-w-0">
            {children}
          </div>
        </div>
      </div>
    </SidebarProvider>
  )
}

/**
 * SessionProviderLayout with loading state
 * Shows loading spinner while session is being restored
 */
interface SessionProviderLayoutWithLoadingProps extends SessionProviderLayoutProps {
  /**
   * Loading state
   */
  isLoading?: boolean
  /**
   * Custom loading component
   */
  loadingComponent?: React.ReactNode
}

/**
 * SessionProviderLayout wrapper with loading state handling
 *
 * @param props - Component props including loading state
 * @returns JSX element with loading state or full layout
 */
export function SessionProviderLayoutWithLoading({
  isLoading = false,
  loadingComponent,
  children,
  ...props
}: SessionProviderLayoutWithLoadingProps) {
  if (isLoading) {
    return (
      <div className="flex min-h-dvh w-full">
        {loadingComponent || (
          <div className="flex items-center justify-center w-full">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-sm text-muted-foreground">Loading workspace...</p>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <SessionProviderLayout {...props}>
      {children}
    </SessionProviderLayout>
  )
}

/**
 * Higher-order component for wrapping pages with SessionProviderLayout
 * Useful for consistent layout application across multiple pages
 *
 * @param WrappedComponent - Component to wrap with layout
 * @param layoutProps - Props to pass to SessionProviderLayout
 * @returns Wrapped component with layout
 */
export function withSessionProviderLayout<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  layoutProps?: Partial<SessionProviderLayoutProps>
) {
  const WithSessionProviderLayout = React.forwardRef<any, P>((props, ref) => {
    return (
      <SessionProviderLayout {...layoutProps}>
        {/* @ts-ignore */}
        <WrappedComponent {...props} ref={ref} />
      </SessionProviderLayout>
    )
  })

  WithSessionProviderLayout.displayName = `withSessionProviderLayout(${WrappedComponent.displayName || WrappedComponent.name || 'Component'
    })`

  return WithSessionProviderLayout
}

/**
 * Hook for accessing layout context (if needed in the future)
 * Placeholder for potential layout-specific state management
 */
export function useSessionProviderLayout() {
  // TODO: Implement layout-specific context if needed
  // For now, this just provides access to layout-related utilities

  return {
    // Placeholder for future layout utilities
    refreshLayout: () => {
      // Force layout refresh if needed
      window.location.reload()
    }
  }
}
